<template>
	<div
		ref="root"
		class="gallery-grid"
	>
		<div
			v-for="(chunk, i) in images"
			:key="i"
			class="gallery-grid__column"
		>
			<FigureGridItem
				v-for="(item, index) in chunk"
				:key="item.content"
				v-bind="item"
				:data-order="calculateTabIndex(index, i) - 1"
				:tabindex="calculateTabIndex(index, i)"
				@click="onItemInteraction(item, calculateTabIndex(index, i) - 1)"
				@keydown.enter.prevent="onItemInteraction(item, calculateTabIndex(index, i) - 1)"
				@keydown="onItemKeyDown($event, calculateTabIndex(index, i) - 1)"
			/>
		</div>
	</div>
</template>
<script lang="ts" setup>
import { chunkArrayHorizontal } from '@/js/utils/arrayUtils';
import FigureGridItem from './internals/FigureGridItem.vue';
import { computed, useTemplateRef } from 'vue';
import type { GalleryElement } from '@/interfaces/components/galleryElement';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import { navigateIndexIn2D } from 'src/utils/grids';

interface Props {
	/** Array of gallery items */
	items: GalleryElement[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
	/** Emitted on image click with payload `[item: GalleryElement, index: number]` */
	itemClick: [item: GalleryElement, index: number];
	/** Emitted on image keydown with payload `[index: number]` */
	itemArrowsNavigation: [index: number];
}>();
const isTwoColumns = computed(() => useBreakpointStore().isMobile);
const cols = computed(() => (isTwoColumns.value ? 2 : 3));
const images = computed(() => chunkArrayHorizontal(props.items, cols.value));
const calculateTabIndex = (index: number, i: number) => index * images.value.length + i + 1;

const galleryRoot = useTemplateRef('root');

const focusElement = (index: number) => {
	const element = galleryRoot.value?.querySelector(`[data-order="${index}"]`) as HTMLElement;
	element?.focus({ preventScroll: true });
};

function onItemInteraction(item: GalleryElement, index: number) {
	emit('itemClick', item, index);
}
function onItemKeyDown(event: KeyboardEvent, currentIndex: number) {
	if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
		event.preventDefault();
		const elementIndex = navigateIndexIn2D(event.key, currentIndex, cols.value, props.items.length);
		focusElement(elementIndex);
		emit('itemArrowsNavigation', elementIndex);
	}
}

defineExpose({
	focusElement,
});
</script>

<style lang="scss" scoped>
.gallery-grid {
	max-width: 134.5rem;
	margin: 0 auto;
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1.6rem;
	padding: 0 0.6rem;

	&__column {
		display: flex;
		flex-direction: column;
		gap: 1.6rem;
	}

	@include media-query-up($breakpoint-small) {
		padding-left: 4.4rem;
		padding-right: 4.4rem;
		grid-template-columns: repeat(3, 1fr);
	}

	@include media-query-up($breakpoint-extralarge) {
		gap: 3.2rem;
		padding: 2.4rem 4.4rem;

		&__column {
			gap: 3.2rem;
		}
	}
}
</style>
